import type { MenuItem } from "@workspace/model"

/**
 * Menu text search — the single matcher behind BOTH the header's suggestion
 * dropdown (`app/[locale]/api/menu-search/route.ts`) and the `/menu?q=` results
 * page. One implementation on purpose: a dropdown that suggests an item the
 * results page then hides reads as a broken search.
 *
 * Pure and side-effect free (no I/O, no request APIs, no `server-only`) so it
 * runs identically in a route handler and in the menu page's RSC render.
 *
 * NOT a relevance engine: there is no backend search operation in the storefront
 * contract, so this is a deliberately small in-memory scan over the tenant's
 * already-cached menu (`getCachedMenu`). Tenant menus are tens-to-hundreds of
 * items; if a tenant ever outgrows that, this is the seam to replace with a real
 * server-side search op.
 */

/** Below this many characters a query is treated as "not searching yet". */
export const MIN_QUERY_LENGTH = 2

/** How many suggestions the dropdown asks for (the results page is uncapped). */
export const SUGGESTION_LIMIT = 8

/**
 * Fold a string into a comparable form: lowercase, diacritics stripped, and the
 * Perso-Arabic look-alikes unified. `fa` tenants (saffron-kitchen) are typed on
 * keyboards that emit Arabic ي/ك where the menu data carries Persian ی/ک — an
 * exact-codepoint match would silently return nothing. Arabic-Indic and Persian
 * digits fold to ASCII for the same reason.
 */
function fold(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[يى]/g, "ی") // ARABIC YEH / ALEF MAKSURA -> FARSI YEH
    .replace(/ك/g, "ک") // ARABIC KAF -> KEHEH
    .replace(/[أإآ]/g, "ا") // hamza/madda alef forms -> ALEF
    .replace(/ة/g, "ه") // TEH MARBUTA -> HEH
    .replace(/[‌‏‎]/g, " ") // ZWNJ / bidi marks -> word break
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660)) // Arabic-Indic digits
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0)) // extended (Persian) digits
    .toLowerCase()
    .replace(/ı/g, "i") // Turkish dotless i -> i (so "ızgara" matches "Izgara")
    .replace(/\s+/g, " ")
    .trim()
}

/** Split a raw query into folded search tokens (empty when nothing to search). */
function tokenize(query: string): string[] {
  const folded = fold(query)
  return folded.length === 0 ? [] : folded.split(" ")
}

/**
 * Score ONE token against one item. 0 means "no match" — every token must score
 * (AND semantics: "chicken pizza" must not match a plain margherita).
 *
 * The tiers are what make the dropdown feel predictable: a name that starts with
 * what you typed outranks a name that merely contains it, which outranks a
 * description-only hit.
 */
function scoreToken(token: string, name: string, description: string): number {
  if (name.startsWith(token)) return 6
  // Word-start inside the name ("… pizza" for "piz").
  if (name.includes(` ${token}`)) return 4
  if (name.includes(token)) return 2
  if (description.startsWith(token) || description.includes(` ${token}`))
    return 1
  if (description.includes(token)) return 0.5
  return 0
}

/**
 * Rank `items` against `query`. Returns a NEW array of matches, best first;
 * an empty array for a too-short/blank query (callers show their idle state
 * rather than dumping the whole menu into a suggestion popover).
 *
 * Ties break on the tenant's own `position` so equally-relevant items keep menu
 * order instead of shuffling between requests.
 */
export function searchMenuItems(
  items: readonly MenuItem[],
  query: string,
  limit?: number
): MenuItem[] {
  const tokens = tokenize(query)
  if (tokens.length === 0 || fold(query).length < MIN_QUERY_LENGTH) return []

  const scored: { item: MenuItem; score: number }[] = []
  for (const item of items) {
    const name = fold(item.name)
    const description = fold(item.shortDescription ?? "")

    let total = 0
    let matchedAll = true
    for (const token of tokens) {
      const score = scoreToken(token, name, description)
      if (score === 0) {
        matchedAll = false
        break
      }
      total += score
    }
    if (matchedAll) scored.push({ item, score: total })
  }

  scored.sort(
    (a, b) =>
      b.score - a.score ||
      (a.item.position ?? 0) - (b.item.position ?? 0) ||
      a.item.name.localeCompare(b.item.name)
  )

  // One row per DISH. A backend menu can list the same item id under more than
  // one department (e.g. "Chicken Kebab" in both Kebabs and Sides); the browse
  // grid wants both (each category section shows its own), but a result list
  // repeating a dish reads as a bug — and duplicate React keys are one.
  const seen = new Set<string>()
  const ranked: MenuItem[] = []
  for (const entry of scored) {
    if (seen.has(entry.item.id)) continue
    seen.add(entry.item.id)
    ranked.push(entry.item)
  }

  return limit === undefined ? ranked : ranked.slice(0, limit)
}

/**
 * One suggestion as it crosses the wire (`/[locale]/api/menu-search`). Already
 * presentation-ready — price is formatted server-side with the request locale so
 * the client component needs no `Intl` pass and no `Money` knowledge.
 */
export interface MenuSearchResult {
  id: string
  /** Where selecting it navigates (`@/lib/item-href`) — a meal deal opens its wizard. */
  href: string
  name: string
  shortDescription?: string
  /** Formatted with the request locale (see `@/lib/money`). */
  price: string
  imageUrl?: string
  imageAlt?: string
  categoryName?: string
  /** Full availability cascade (item + required modifier groups), not `item.available`. */
  available: boolean
}

/** The suggestion endpoint's response body. */
export interface MenuSearchResponse {
  results: MenuSearchResult[]
}

/** Normalize a raw `?q=` value; empty string when there is nothing to search. */
export function normalizeSearchQuery(
  raw: string | string[] | undefined
): string {
  const value = Array.isArray(raw) ? raw[0] : raw
  const trimmed = value?.trim() ?? ""
  return fold(trimmed).length < MIN_QUERY_LENGTH ? "" : trimmed
}
